// Small pure helpers for reading a Supabase `auth.mfa.listFactors()` result —
// shared by the RD-SEC-015 MFA stepper (`components/auth/mfa/*`),
// `MfaEncourageBanner`, and `MfaVerifyScreen`. Moved here from
// `components/admin/settings/twoFactorSetupStyles.js` (RD-SEC-015 UI fix
// batch, 2026-09-25) — that file's style-token exports were deleted with the
// legacy enrollment UI, leaving only these two framework-agnostic functions,
// which belong under `utils/auth/` with the rest of the MFA helpers, not
// under an admin-settings component path.

/**
 * RD-SEC-015 — a user CAN hold two verified TOTP factors at once (the
 * change-phone flow keeps the old one enrolled until the new one verifies,
 * so there's a window with two). `verified` must be the NEWEST one — an
 * unenroll race that removes the old factor after a background
 * `listFactors()` re-fetch must never leave the UI pointing at the factor
 * that's about to disappear. `verifiedAll` exposes every verified factor for
 * callers that need the full set (e.g. counting or unenrolling "every other"
 * factor in the change-phone flow). Sorted by `created_at` descending, so a
 * missing/equal timestamp falls back to array order (still deterministic).
 */
export function totpFactorsFromList(data) {
  if (!data?.all) return { verified: null, verifiedAll: [], unverified: null };
  const totp = data.all.filter((x) => x.factor_type === 'totp');
  const byNewestFirst = (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  const verifiedAll = totp.filter((x) => x.status === 'verified').sort(byNewestFirst);
  const unverifiedAll = totp.filter((x) => x.status === 'unverified').sort(byNewestFirst);
  return {
    verified: verifiedAll[0] ?? null,
    verifiedAll,
    unverified: unverifiedAll[0] ?? null,
  };
}

// Supabase JS sets `totp.qr_code` to `data:image/svg+xml;utf-8,` + raw SVG.
// Don't prefix or encodeURIComponent the whole value — that breaks the image.
export function totpQrImageSrc(qrCode) {
  if (qrCode == null || qrCode === '') return '';
  const s = String(qrCode).trim();
  if (s.startsWith('data:image/')) return s;
  if (s.startsWith('<')) return `data:image/svg+xml;utf-8,${encodeURIComponent(s)}`;
  return `data:image/svg+xml;utf-8,${encodeURIComponent(s)}`;
}
